<script setup lang="ts">
import { resetForm } from '@/helpers/ep/form'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'
import { useForemanClientsToSiteMappings } from '@/composables/core/foreman-site-mappings'
import type { IForemanClientsToSiteMappingForm } from '@/types/core/forms'
import { SiteType } from '@/types/core/Site'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { getForemanClients } from '@/services/core/foreman-site-mappings'
import type { IForemanClient } from '@/types/core/ForemanSiteMapping'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'

const selectedSiteRef = ref<ISiteIdentifier>()
const selectedForemanClientsRef = ref<IForemanClient[]>([])
const foremanClientsLoading = ref<boolean>(false)
const foremanClientOptions = ref<OptionType[]>([])

const { createMappingMutation } = useForemanClientsToSiteMappings()
const { isPending: createIsPending, mutateAsync: createAsync } = createMappingMutation

const open = defineModel<boolean>()
const emit = defineEmits(['closed'])

const mappingFormRef = ref<FormInstance>()
const mappingForm = reactive<IForemanClientsToSiteMappingForm>({
  site_id: undefined,
  foreman_client_ids: undefined,
  foreman_client_names: undefined,
})

const rules = reactive<FormRules<IForemanClientsToSiteMappingForm>>({
  site_id: [{ required: true, message: 'Site is required.', trigger: ['blur', 'change'] }],
  foreman_client_ids: [{ required: true, message: 'Foreman Client IDs are required.', trigger: ['blur', 'change'] }],
})

const submitForm = async (formEl: FormInstance | undefined) => {
  formEl?.validate(async (valid) => {
    if (!valid) {
      return
    }

    try {
      await createAsync(mappingForm)
      handleClosed()
      open.value = false
    } catch {
      // handled in UI
    }
  })
}

/**
 * Reset form
 */
const handleClosed = () => {
  resetForm(mappingFormRef.value)
  selectedSiteRef.value = undefined
  selectedForemanClientsRef.value = []
  createMappingMutation.reset()
  emit('closed')
}

const onSiteChange = (val: any) => {
  if (val) {
    selectedSiteRef.value = val
    mappingForm.site_id = val.site_id
    return
  }

  mappingForm.site_id = undefined
  selectedSiteRef.value = undefined
}

const onForemanClientsChange = (val: any) => {
  if (val) {
    selectedForemanClientsRef.value = val
    mappingForm.foreman_client_ids = val.map((client: IForemanClient) => client.id)
    mappingForm.foreman_client_names = val.map((client: IForemanClient) => client.name)
    return
  }

  mappingForm.foreman_client_ids = undefined
  mappingForm.foreman_client_names = undefined
  selectedForemanClientsRef.value = []
}

const handleForemanClientsSearch = async (query: string) => {
  try {
    foremanClientsLoading.value = true
    const clients = await getForemanClients()
    foremanClientOptions.value = clients
      .filter(
        (c: IForemanClient) => c.name.toLowerCase().includes(query.toLowerCase()) || c.id.toString().includes(query)
      )
      .map((client: IForemanClient) => {
        return {
          label: `${client.id} - ${client.name}`,
          value: client,
        }
      })
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to search Foreman clients.',
      grouping: true,
    })
  } finally {
    foremanClientsLoading.value = false
  }
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    title="Create Site to Foreman Clients mapping"
    :show-close="false"
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <el-form
      ref="mappingFormRef"
      :model="mappingForm"
      :rules="rules"
      :label-width="160"
      label-position="left"
      :disabled="createIsPending"
    >
      <el-form-item prop="site_id" label="Site">
        <RcSiteSelect v-model="selectedSiteRef" :type="SiteType.DataCenter" @change="onSiteChange" />
      </el-form-item>
      <el-form-item prop="foreman_client_ids" label="Foreman Clients">
        <el-select-v2
          v-model="selectedForemanClientsRef"
          :options="foremanClientOptions"
          filterable
          remote
          multiple
          :remote-method="handleForemanClientsSearch"
          clearable
          :loading="foremanClientsLoading"
          no-data-text="No clients found"
          placeholder="Choose clients"
          value-key="id"
          @change="onForemanClientsChange"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="createIsPending || !open" @click="open = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="createIsPending"
        :disabled="createIsPending || !open"
        @click="submitForm(mappingFormRef)"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>
